import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Plus, ExternalLink, Loader2, Calendar, FileText } from 'lucide-react';
import { legacyApi, RelatedOpenSaleOrder } from '../services/legacyApi';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { RelatedOpenSaleOrdersNotice } from '../modules/legacy-core/components/RelatedOpenSaleOrdersNotice';

interface EstimateOrdersModalProps {
    isOpen: boolean;
    onClose: () => void;
    estimateId: number;
    estimateRef?: string;
    customerName?: string;
    customerId?: number;
    revisionNo?: number;
}

export const EstimateOrdersModal: React.FC<EstimateOrdersModalProps> = ({ isOpen, onClose, estimateId, estimateRef, customerName, customerId, revisionNo }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [relatedOpenOrders, setRelatedOpenOrders] = useState<RelatedOpenSaleOrder[]>([]);

    // Form States
    const [poNumber, setPoNumber] = useState("");
    const [soDate, setSoDate] = useState(new Date().toISOString().split('T')[0]);

    useEffect(() => {
        let cancelled = false;
        if (!isOpen || !customerId) {
            setRelatedOpenOrders([]);
            return () => {
                cancelled = true;
            };
        }
        legacyApi.sales.listRelatedOpenOrders(Number(customerId))
            .then((rows) => {
                if (!cancelled) setRelatedOpenOrders(Array.isArray(rows) ? rows : []);
            })
            .catch(() => {
                if (!cancelled) setRelatedOpenOrders([]);
            });
        return () => {
            cancelled = true;
        };
    }, [customerId, isOpen]);

    const handleCreate = async () => {
        if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
            console.log("Creating order for Est:", estimateId);
        }

        setLoading(true);
        try {
            const newOrder = await legacyApi.sales.convertEstimate({
                estimate_id: estimateId,
                customer_po_no: poNumber || "TBD",
                so_date: soDate,
                so_status_id: 1, // Default Confirmed
                allow_duplicate: true
            });
            toast.success("Sale Order Created Successfully");
            onClose();
            if (newOrder && newOrder.so_id) {
                navigate(`/legacy/orders/${newOrder.so_id}`);
            }
        } catch (err: any) {
            console.error(err);
            toast.error(err.message || "Failed to create order");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>Convert to Sale Order</DialogTitle>
                </DialogHeader>

                <div className="py-4 space-y-4">
                    <div className="space-y-1">
                        <label className="text-sm font-medium text-gray-700">Estimate Reference</label>
                        <div className="text-sm text-gray-900 bg-gray-50 p-2 rounded border">
                            {estimateRef || `HC/EST/${String(estimateId).padStart(4, '0')}${(revisionNo ?? 0) > 0 ? `/R${revisionNo}` : ''}`} - {customerName}
                        </div>
                    </div>

                    <div className="space-y-1">
                        <label className="text-sm font-medium text-gray-700">Customer PO Number</label>
                        <input
                            type="text"
                            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Enter PO Number (Optional)"
                            value={poNumber}
                            onChange={(e) => setPoNumber(e.target.value)}
                        />
                    </div>

                    <div className="space-y-1">
                        <label className="text-sm font-medium text-gray-700">Order Date</label>
                        <input
                            type="date"
                            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={soDate}
                            onChange={(e) => setSoDate(e.target.value)}
                        />
                    </div>

                    <RelatedOpenSaleOrdersNotice orders={relatedOpenOrders} customerName={customerName} />
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={loading}>Cancel</Button>
                    <Button onClick={handleCreate} disabled={loading}>
                        {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                        Create Order
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
